import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import { I18nLanguage, getI18nLanguageList } from './types/i18nLanguage.ts';
import { I18nNamespace } from './types/i18nNamespace.ts';
import { selectedLanguage, setLanguage } from '@/features/language/signals.ts';

type TranslationResource = Record<string, unknown>;
type DictionaryModule = {
  default: TranslationResource;
};
type DictionaryLoader = () => Promise<DictionaryModule>;

const coreNamespaces: I18nNamespace[] = [I18nNamespace.Common, I18nNamespace.Core, I18nNamespace.Validation];
const allNamespaces: I18nNamespace[] = [
  I18nNamespace.Common,
  I18nNamespace.Core,
  I18nNamespace.UserManagement,
  I18nNamespace.Validation,
  I18nNamespace.CourseManagement,
  I18nNamespace.ScenarioSection,
  I18nNamespace.GroupManagement,
  I18nNamespace.ScheduledCalls,
  I18nNamespace.GlobalAssets,
  I18nNamespace.Dashboard,
  I18nNamespace.ScenarioEditor,
  I18nNamespace.MyDashboard,
  I18nNamespace.Translator,
  I18nNamespace.EvaluationProtocols,
  I18nNamespace.ScenarioPlayer,
  I18nNamespace.CompanyManagement,
  I18nNamespace.Personas,
  I18nNamespace.DecisionSupport,
  I18nNamespace.Exercises
];

const namespaceSet = new Set<string>(allNamespaces);
const dictionaryLoaders: Record<string, DictionaryLoader> = import.meta.glob<DictionaryModule>('./dictionaries/*/*.ts');

function resolveDictionaryLanguage(language?: string): I18nLanguage {
  const normalized = (language ?? '').toLowerCase();

  if (normalized === I18nLanguage.German || normalized.startsWith(`${I18nLanguage.German}-`)) {
    return I18nLanguage.German;
  }

  if (
    normalized === I18nLanguage.Norwegian ||
    normalized.startsWith(`${I18nLanguage.Norwegian}-`) ||
    normalized.startsWith('nb-') ||
    normalized === 'nb' ||
    normalized.startsWith('nn-') ||
    normalized === 'nn'
  ) {
    return I18nLanguage.Norwegian;
  }

  return I18nLanguage.English;
}

function isI18nNamespace(namespace: string): namespace is I18nNamespace {
  return namespaceSet.has(namespace);
}

function getDictionaryModulePath(namespace: I18nNamespace, language: I18nLanguage) {
  return `./dictionaries/${namespace}/${language}.ts`;
}

async function loadDictionaryResource(namespace: I18nNamespace, language: I18nLanguage): Promise<TranslationResource> {
  const primaryPath = getDictionaryModulePath(namespace, language);
  const primaryLoader = dictionaryLoaders[primaryPath];

  if (primaryLoader) {
    const module = await primaryLoader();
    return module.default;
  }

  const fallbackPath = getDictionaryModulePath(namespace, I18nLanguage.English);
  const fallbackLoader = dictionaryLoaders[fallbackPath];
  if (fallbackLoader) {
    const module = await fallbackLoader();
    return module.default;
  }

  throw new Error(`Missing i18n dictionary "${namespace}" for languages "${language}" and "${I18nLanguage.English}"`);
}

const dynamicResourceBackend = {
  type: 'backend' as const,
  read: async (
    language: string,
    namespace: string,
    callback: (error: Error | null, resources?: TranslationResource) => void
  ) => {
    if (!isI18nNamespace(namespace)) {
      callback(new Error(`Unknown i18n namespace "${namespace}"`));
      return;
    }

    try {
      const resolvedLanguage = resolveDictionaryLanguage(language);
      const resources = await loadDictionaryResource(namespace, resolvedLanguage);
      callback(null, resources);
    } catch (error) {
      callback(error instanceof Error ? error : new Error(`Failed to load ${language}/${namespace}`));
    }
  }
};

// Fancy stuff like language auto-detection, additional formatters and backend loading is all added here
void i18n
  .use(LanguageDetector)
  .use(dynamicResourceBackend)
  .use(initReactI18next)
  .init({
    debug: false,
    fallbackLng: I18nLanguage.English,
    supportedLngs: getI18nLanguageList(),
    whitelist: getI18nLanguageList(),
    ns: coreNamespaces,
    defaultNS: I18nNamespace.Common,
    // React already escapes output. Prevent i18next from HTML-escaping
    // interpolated values like '/' -> '&#x2F;'.
    interpolation: { escapeValue: false },
    react: {
      useSuspense: false
    },

    detection: {
      order: ['localStorage', 'navigator'], // Use browser language if user has not actively selected language
      caches: ['localStorage'],
      lookupLocalStorage: 'userLocalization', // custom lookup key
      checkWhitelist: true // only detect languages that are in the whitelist
    }
  });

// Listen for language changes and update the signal
i18n.on('languageChanged', (lng) => {
  setLanguage(resolveDictionaryLanguage(lng));
});

// Set the initial language value
selectedLanguage.value = resolveDictionaryLanguage(i18n.language);

export default i18n;
